import { keepPreviousData } from "@tanstack/react-query";
import { BatchCall, useContractView } from "logic/hooks";
import { useMemo } from "react";
import { erc20Abi } from "viem";

import { LOAN_ADDRESSES, LOAN_ROUTER_V2_ABI, ReserveAccountAbi } from "../contracts";
import { Loan } from "../data/types";
import { useLoanDetails } from "../LoanDetails/LoanDetailsProvider";

export function useBatchRepayTx(loan: Loan, customPaymentAmount?: bigint, transferAmount?: bigint) {
  const { reserveAccount: resAccAddress, tokens, chainId } = useLoanDetails();

  const paymentAmount = usePaymentQuote(loan, customPaymentAmount);

  /**************************************************************************/
  /* Batched Calls */
  /**************************************************************************/
  const calls = useMemo(() => {
    const _calls: BatchCall[] = [];
    if (transferAmount != undefined && transferAmount > 0n) {
      _calls.push({
        address: tokens.usdai.main.address,
        abi: erc20Abi,
        functionName: "transfer",
        args: [resAccAddress, transferAmount],
        chainId,
      });
    }
    _calls.push({
      address: resAccAddress,
      abi: ReserveAccountAbi,
      functionName: "repay",
      args: [loan.termsStruct, paymentAmount],
      chainId,
    });

    return _calls;
  }, [loan.termsStruct, paymentAmount, transferAmount, resAccAddress, tokens.usdai.main.address, chainId]);

  return { calls, error: null };
}

const QUOTE_DEADLINE_OFFSET_SEC = 180;
const QUOTE_BUCKET_SEC = 30;

// Snap to a 30s wall-clock bucket so `deadline` (and therefore the queryKey)
// is stable across renders inside one bucket. Previously this was derived
// from a per-block `useBlock`, which made the queryKey churn every ~8s and
// defeated the 30s `refetchInterval` below — so `paymentAmount` ticked up
// (interest accrual) far more often than intended, contributing to the
// "Insufficient Balance" flip on the repay button. See useBridgeAndRepay.ts
// for the active instrumentation tracking which mechanism is actually firing.
export function usePaymentQuote(loan: Loan, customPaymentAmount?: bigint): bigint | undefined {
  const { chainId } = loan;
  const { loanRouterV2 } = LOAN_ADDRESSES[chainId];

  const nowSec = Math.floor(Date.now() / 1000);
  const bucketSec = nowSec - (nowSec % QUOTE_BUCKET_SEC);
  const deadline = BigInt(bucketSec + QUOTE_DEADLINE_OFFSET_SEC);

  const { data: quote } = useContractView({
    address: loanRouterV2,
    abi: LOAN_ROUTER_V2_ABI,
    functionName: "quote",
    args: [loan.termsStruct, deadline],
    chainId,
    query: {
      placeholderData: keepPreviousData,
      select: (data) => BigInt(data[0] + data[1] + data[2]),
      refetchInterval: 30_000,
      enabled: customPaymentAmount == undefined,
    },
  });

  return customPaymentAmount ?? quote;
}
